import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Plus, MessageCircle, User, LogOut, Search, Eye } from 'lucide-react';
import { Header } from '@/components/Header';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardAcquereur = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [myAds, setMyAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyAds();
  }, []);

  const fetchMyAds = async () => {
    try {
      const response = await axios.get(`${API}/acquirer-ads/my`, {
        withCredentials: true
      });
      setMyAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Erreur lors du chargement de vos recherches');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => navigate('/create-search')}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl mb-6">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-3">
            Bienvenue, {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            Gérez vos recherches et consultez vos propositions
          </p>
        </div>

        {/* Quick Stats */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl mb-6">
          <h3 className="text-xl font-bold text-blue-600 mb-4">\ud83d\udcca Mes statistiques</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            <div className="bg-blue-50 p-4 rounded-xl text-center">
              <div className="flex items-center justify-center mb-2">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-xs md:text-sm text-gray-600 mb-1">Recherches actives</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800\">{myAds.filter(ad => ad.status === 'active').length}</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-xl text-center">
              <div className="flex items-center justify-center mb-2">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-xs md:text-sm text-gray-600 mb-1">Propositions</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800\">
                {myAds.reduce((sum, ad) => sum + ad.proposals_received, 0)}
              </p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-xl text-center">
              <div className="flex items-center justify-center mb-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-xs md:text-sm text-gray-600 mb-1">Messages</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-800\">0</p>
            </div>
          </div>
        </div>

        {/* My Searches */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl md:text-2xl font-bold text-blue-600">
              📝 Mes recherches
            </h3>
            <Button
              onClick={() => navigate('/create-search')}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 md:px-6 py-2 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </div>
        </div>

        {/* Searches Grid */}
        {myAds.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-6 flex items-center justify-center">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h4 className="font-manrope text-xl font-bold text-foreground mb-2">
              Aucune recherche active
            </h4>
            <p className="text-muted-foreground mb-6">
              Créez votre première fiche de recherche pour recevoir des propositions exclusives
            </p>
            <Button
              onClick={() => navigate('/create-search')}
              className="rounded-full font-manrope font-semibold"
              data-testid="empty-state-create-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer ma première recherche
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {myAds.map((ad) => (
              <div
                key={ad.ad_id}
                className="bg-white border border-border rounded-2xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer group"
                data-testid={`ad-card-${ad.ad_id}`}
              >
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-manrope font-bold text-foreground group-hover:text-primary transition-colors">
                      {ad.title}
                    </h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-manrope font-semibold uppercase tracking-wider ${
                      ad.status === 'active' 
                        ? 'bg-success/20 text-success' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {ad.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Type:</span>
                      <span>{ad.property_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Budget:</span>
                      <span className="font-mono">{ad.max_budget.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Localisation:</span>
                      <span>{ad.location}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {ad.proposals_received} proposition{ad.proposals_received !== 1 ? 's' : ''} reçue{ad.proposals_received !== 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary hover:bg-primary/10 font-manrope font-semibold"
                    data-testid={`view-ad-${ad.ad_id}`}
                  >
                    Voir détails
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardAcquereur;
